import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';
import './MobileProfilePage.css';

const maskEmail = (email) => {
  if (!email || !email.includes('@')) return '—';
  const [local, domain] = email.split('@');
  if (local.length <= 2) return `**@${domain}`;
  if (local.length <= 4) return `${local[0]}****@${domain}`;
  return `${local.slice(0, 3)}****${local.slice(-1)}@${domain}`;
};

const resolveAvatar = (u) => {
  const a = u?.avatar || u?.logo || u?.profileImage;
  if (!a) return null;
  if (a.startsWith('http') || a.startsWith('data:')) return a;
  if (a.startsWith('/uploads')) return a;
  return `${API_BASE_URL}${a.startsWith('/') ? a : `/${a}`}`;
};

const Chevron = () => (
  <svg className="mpf-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const MobileProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    || user?.name
    || user?.companyName
    || user?.email?.split('@')[0]
    || 'User';
  const initial = (displayName || 'U').charAt(0).toUpperCase();
  const emailMasked = useMemo(() => maskEmail(user?.email), [user?.email]);
  const phone = user?.phone || user?.mobile;
  const country = (user?.country || 'IN').toString();
  const buyerType = user?.isSupplier
    ? 'Supplier'
    : (user?.isBuyer !== false ? 'Buyer' : 'Account');

  const goFullProfile = (hash) => {
    navigate('/profile', { state: { fromMobile: true, focus: hash } });
  };

  const avatarUrl = resolveAvatar(user);

  return (
    <div className="mpf-page">
      <header className="mpf-header">
        <button type="button" className="mpf-back" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="mpf-header-title">Profile</h1>
      </header>

      <div className="mpf-hero">
        <div className="mpf-avatar-wrap">
          <div className="mpf-avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <button
            type="button"
            className="mpf-avatar-edit"
            aria-label="Change photo"
            onClick={() => goFullProfile('media')}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
        </div>
        <div className="mpf-hero-right">
          <div className="mpf-hero-top">
            <h2 className="mpf-name">{displayName}</h2>
            <button
              type="button"
              className="mpf-qr-btn"
              aria-label="Profile QR"
              onClick={() => goFullProfile('qr')}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h6v6H3V3zm2 2v2h2V5H5zm8-2h6v6h-6V3zm2 2v2h2V5h-2zM3 15h6v6H3v-6zm2 2v2h2v-2H5zm13-2h2v2h-2v-2zm-2 2h2v2h-2v-2zm2 2h2v2h-2v-2zM15 3h2v2h-2V3zm2 2h2v2h-2V5zm-2 12h2v2h-2v-2zm-2-8h2v2h-2V9z" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            className="mpf-buyer-pill"
            onClick={() => goFullProfile('type')}
          >
            + {buyerType} type
          </button>
          <div className="mpf-loc">
            <span role="img" aria-label="country">🇮🇳</span>
            <span>{country}</span>
            <button
              type="button"
              style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: '#999' }}
              aria-label="Location info"
              onClick={() => navigate('/settings')}
            >
              ⓘ
            </button>
          </div>
        </div>
      </div>

      <div className="mpf-section">
        <button type="button" className="mpf-row" onClick={() => navigate('/mobile/profile/full-name')}>
          <span className="mpf-row-label">Full name</span>
          <span className="mpf-row-value">{displayName}<Chevron /></span>
        </button>
        <button type="button" className="mpf-row" onClick={() => goFullProfile('email')}>
          <span className="mpf-row-label">Email</span>
          <span className="mpf-row-value">{emailMasked}<Chevron /></span>
        </button>
        <button type="button" className="mpf-row" onClick={() => goFullProfile('phone')}>
          <span className="mpf-row-label">Phone number</span>
          <span className={`mpf-row-value ${!phone ? 'mpf-muted' : ''}`}>
            {phone || 'Incomplete'}
            <Chevron />
          </span>
        </button>
        <button type="button" className="mpf-row" onClick={() => goFullProfile('password')}>
          <span className="mpf-row-label">Change password</span>
          <Chevron />
        </button>
      </div>

      <p className="mpf-sub" style={{ marginTop: 10, borderTop: '1px solid #eee' }}>
        <strong>Connected accounts</strong>
        <br />
        For faster sign-in, connect your account to these services.
      </p>
      <div className="mpf-section" style={{ marginTop: 0, borderTop: 'none' }}>
        <button type="button" className="mpf-connect" onClick={() => goFullProfile('connect-google')}>
          <div className="mpf-connect-left">
            <span className="mpf-connect-name">G Google</span>
          </div>
          <span className="mpf-connect-action">Connect <Chevron /></span>
        </button>
        <button type="button" className="mpf-connect" onClick={() => goFullProfile('connect-facebook')}>
          <div className="mpf-connect-left">
            <span className="mpf-connect-name">f Facebook</span>
          </div>
          <span className="mpf-connect-action">Connect <Chevron /></span>
        </button>
      </div>

      <div className="mpf-section">
        <button
          type="button"
          className="mpf-section-h"
          onClick={() => goFullProfile('sourcing')}
        >
          <span>
            Sourcing preferences
            <span className="mpf-incomplete">Incomplete</span>
          </span>
          <Chevron />
        </button>
        <button type="button" className="mpf-promo" onClick={() => navigate('/products')}>
          <div className="mpf-promo-icon">♥</div>
          <div>
            <p className="mpf-promo-title">Get personalized product suggestions</p>
            <p className="mpf-promo-desc">Fill out a short questionnaire to enhance your sourcing experience.</p>
          </div>
        </button>
      </div>

      <div className="mpf-section">
        <button
          type="button"
          className="mpf-section-h"
          onClick={() => goFullProfile('business')}
        >
          <span>Business information</span>
          <Chevron />
        </button>
        <p className="mpf-sub">Complete your profile for a better sourcing experience</p>
        <div className="mpf-grey-box">
          <button type="button" className="mpf-mini-row" onClick={() => goFullProfile('business')}>
            <div className="mpf-mini-left">👤 Business contact</div>
            <span className="mpf-incomplete" style={{ margin: 0 }}>Incomplete <Chevron /></span>
          </button>
          <button type="button" className="mpf-mini-row" onClick={() => goFullProfile('verification')}>
            <div className="mpf-mini-left">🪪 Verification</div>
            <span className="mpf-incomplete" style={{ margin: 0 }}>Incomplete <Chevron /></span>
          </button>
          <button type="button" className="mpf-mini-row" onClick={() => navigate('/mobile/shipping-address')}>
            <div className="mpf-mini-left">📍 {country}</div>
            <span style={{ color: '#333' }}>Shipping <Chevron /></span>
          </button>
        </div>
      </div>

      <div className="mpf-section">
        <button
          type="button"
          className="mpf-section-h"
          onClick={() => navigate('/mobile/tax-information')}
        >
          <span>
            Tax information
            <span className="mpf-incomplete">Incomplete</span>
          </span>
          <Chevron />
        </button>
        <p className="mpf-tax-note">Complete tax information to get verified for tax-exempt status.</p>
      </div>
    </div>
  );
};

export default MobileProfilePage;
